import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { MdOutlineFileUpload, MdOutlineShield, MdOutlineEditNote } from "react-icons/md";
import {
  acceptContractByOtpAPI,
  sendContractOtpAPI,
  uploadOnboardingContractAPI,
} from "services/user-controllers/onboardingController";

const MAX_BYTES = 10 * 1024 * 1024;
const OK_TYPES = [".pdf", ".png", ".jpg", ".jpeg"];

// "Choose Contract Acceptance Method": 1) accept the standard agreement with an email OTP, or
// 2) upload a custom signed agreement. Either one hands the contract to MV Load for verification.
const ContractAcceptance = ({ email, onDone }) => {
  const fileRef = useRef(null);
  const [tab, setTab] = useState("otp");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(""); // text of what is happening, empty when idle
  const lock = useRef(false); // set synchronously so a fast double-click cannot submit twice

  const run = async (text, fn) => {
    if (lock.current) return;
    lock.current = true;
    try {
      setBusy(text);
      await fn();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      lock.current = false;
      setBusy("");
    }
  };

  const sendOtp = () =>
    run("Sending the OTP to your email…", async () => {
      const { data } = await sendContractOtpAPI();
      if (data?.code === 200) {
        setOtpSent(true);
        setOtp("");
        toast.success(`OTP sent to ${email}`);
      } else toast.error(data?.message || "Could not send the OTP");
    });

  const acceptWithOtp = () =>
    run("Verifying the OTP and accepting your contract…", async () => {
      const { data } = await acceptContractByOtpAPI(otp.trim());
      if (data?.code === 200) {
        toast.success("Contract accepted");
        onDone();
      } else toast.error(data?.message || "Invalid OTP");
    });

  const pick = (f) => {
    if (!f) return;
    const ext = f.name.slice(f.name.lastIndexOf(".")).toLowerCase();
    if (!OK_TYPES.includes(ext)) return toast.error("Upload a PDF, JPG or PNG file");
    if (f.size > MAX_BYTES) return toast.error("File must be under 10 MB");
    setFile(f);
  };

  const submitUpload = () =>
    run("Uploading your signed contract…", async () => {
      if (!file) return toast.error("Please choose the signed contract first");
      const form = new FormData();
      form.append("contract", file);
      const { data } = await uploadOnboardingContractAPI(form);
      if (data?.code === 200) {
        toast.success("Signed contract uploaded");
        setFile(null);
        onDone();
      } else toast.error(data?.message || "Upload failed");
    });

  return (
    <div className="ob_card ct_accept">
      {busy && (
        <div className="ct_overlay" role="alert" aria-busy="true">
          <div className="ct_overlay_box">
            <span className="ct_spinner" />
            <p>{busy}</p>
          </div>
        </div>
      )}
      <div className="ct_accept_head">
        <div>
          <h5>Choose Contract Acceptance Method</h5>
          <p>Accept our standard logistics agreement instantly via email OTP or upload your custom signed agreement document.</p>
        </div>
        <div className="ct_tabs">
          <button type="button" className={tab === "otp" ? "on" : ""} onClick={() => setTab("otp")}>
            1. Accept Standard Contract (OTP)
          </button>
          <button type="button" className={tab === "upload" ? "on" : ""} onClick={() => setTab("upload")}>
            2. Upload Custom Contract
          </button>
        </div>
      </div>

      {tab === "otp" && (
        <div className="ct_otp_panel">
          <div className="ct_otp_row">
            <div className="ct_otp_text">
              <h6>
                <MdOutlineShield /> Accept Company Standard Contract via Email OTP
              </h6>
              <p>
                By clicking <strong>"Accept Contract &amp; Send OTP"</strong>, an authorization code will be emailed to{" "}
                <strong>{email}</strong>.
                <br />
                Entering this code completes legal agreement acceptance from both ends instantly.
              </p>
            </div>
            <button type="button" className="ob_btn primary ct_big" disabled={!!busy} onClick={sendOtp}>
              <MdOutlineEditNote /> {busy ? "Please wait…" : otpSent ? "Resend OTP" : "Accept Contract & Send OTP"}
            </button>
          </div>
          {otpSent && (
            <div className="ct_otp_enter">
              <label>Enter the OTP sent to your email (valid for 10 minutes)</label>
              <div className="row">
                <input
                  value={otp}
                  maxLength={6}
                  inputMode="numeric"
                  placeholder="Enter OTP"
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                />
                <button type="button" className="ob_btn primary" disabled={!!busy || !otp} onClick={acceptWithOtp}>
                  {busy ? "Please wait…" : "Verify & Accept Contract"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === "upload" && (
        <div className="ct_upload_panel">
          <h6>
            <MdOutlineFileUpload /> Upload Custom Signed Contract
          </h6>
          <p>Print or export your custom contract → sign &amp; stamp → upload the scanned file below.</p>
          <div
            className={`ct_drop ${dragging ? "drag" : ""}`}
            onClick={() => fileRef.current && fileRef.current.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              pick(e.dataTransfer.files && e.dataTransfer.files[0]);
            }}
          >
            <MdOutlineFileUpload className="ct_drop_icon" />
            {file ? (
              <strong>{file.name}</strong>
            ) : (
              <strong>
                Drag &amp; drop or <span>click to browse</span>
              </strong>
            )}
            <small>PDF, JPG, PNG — max 10MB</small>
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" hidden onChange={(e) => pick(e.target.files && e.target.files[0])} />
          </div>
          <div className="ct_submit">
            <button type="button" className="ob_btn primary ct_big" disabled={!!busy || !file} onClick={submitUpload}>
              <MdOutlineFileUpload /> {busy ? "Uploading…" : "Submit Custom Contract"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractAcceptance;
